import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { toast } from '../lib/toast';
import * as api from './api';
import { zenKeys } from './hooks';
import type { DepartmentTask, WorkOrder, ZenBootstrap, ZenUser } from './types';

const MESSAGES: Record<api.TaskActionName, string> = {
  start: 'started',
  finish: 'finished',
  'admin-release': 'released back to Pending',
  'admin-complete': 'marked done',
  'admin-reopen': 'reopened',
};

export async function executeTaskAction(
  send: () => Promise<unknown>,
  refresh: () => Promise<unknown>,
): Promise<void> {
  await send();
  await refresh();
}

/** Task state changes are committed immediately so later actions always see the server's latest state. */
export function useTaskActions(user: ZenUser | undefined) {
  const queryClient = useQueryClient();

  return useCallback(async (task: DepartmentTask, action: api.TaskActionName, order: WorkOrder) => {
    if (!user) return;
    const refresh = () => Promise.all([
      queryClient.invalidateQueries({ queryKey: zenKeys.bootstrap }),
      queryClient.invalidateQueries({ queryKey: zenKeys.order(order.id) }),
    ]);
    try {
      await executeTaskAction(() => api.taskAction(task.id, action), refresh);
      toast.success(`${order.orderNumber} · ${task.departmentName} ${MESSAGES[action]}`);
    } catch (error) {
      await refresh();
      const fresh = queryClient.getQueryData<ZenBootstrap>(zenKeys.bootstrap)?.tasks.find((item) => item.id === task.id);
      if (action === 'start' && fresh?.status === 'started' && fresh.pickedUpBy !== user.id) {
        toast.error(`${fresh.pickedUpByName ?? 'Someone else'} started ${order.orderNumber} first. It's in their queue now.`);
        return;
      }
      toast.error(error instanceof Error ? error.message : `Couldn’t update ${order.orderNumber}. Try again.`);
    }
  }, [queryClient, user]);
}
